/**
 * <<uppercase_module_name>> Module
 */
import Vue from "vue";
import Vuex from "vuex";
import api from "Api";
import router from "Router";
import app from "Main";

// import the api endpoints
import {
    getSqlFields,
    getSqlField,
    printSqlField,
    createSqlFields,
    updateSqlFields,
    deleteSqlFields,
} from "Core/configuration/developer/config/api.sql_fields";

const state = {
    page: {},
    form: {},
    sql_fields: [],
    sql_field_options: [],
    selectedSqlField: {
        field_id: null,
        company_id: "",
        sql_field_date: "",
        reference_number: "",
        status_id: "",
        billing_address_id: "",
        shipping_address_id: "",
        comments: "",
        sql_field_details: [],
    },
    sql_fields_filter: {
        search: "",
        page_size: 2,
        page_num: 1,
    },
    rules_sql_fields: {
        table_id: {
            name: "table_id",
            rules: "trim|max_length[15]|integer|required",
            max_len: "15",
            min_len: "",
            default_value: "",
        },
        position: {
            name: "position",
            rules: "trim|max_length[4]|integer",
            max_len: "4",
            min_len: "",
            default_value: "",
        },
        name: {
            name: "name",
            rules: "trim|max_length[100]|required",
            max_len: "100",
            min_len: "",
            default_value: "",
        },
        alias: {
            name: "alias",
            rules: "trim|max_length[100]|required",
            max_len: "100",
            min_len: "",
            default_value: "",
        },
        placeholder: {
            name: "placeholder",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        example: {
            name: "example",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        rules: {
            name: "rules",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        form_rules: {
            name: "form_rules",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        fillable: {
            name: "fillable",
            rules: "trim|max_length[1]|integer|required",
            max_len: "1",
            min_len: "",
            default_value: "1",
        },
        protected: {
            name: "protected",
            rules: "trim|max_length[1]|integer|required",
            max_len: "1",
            min_len: "",
            default_value: "",
        },
        is_hidden: {
            name: "is_hidden",
            rules: "trim|max_length[1]|integer|required",
            max_len: "1",
            min_len: "",
            default_value: "",
        },
        is_searchable: {
            name: "is_searchable",
            rules: "trim|max_length[1]|integer|required",
            max_len: "1",
            min_len: "",
            default_value: "",
        },
        type: {
            name: "type",
            rules: "trim|max_length[12]|required",
            max_len: "12",
            min_len: "",
            default_value: "",
        },
        input_type: {
            name: "input_type",
            rules: "trim|max_length[45]",
            max_len: "45",
            min_len: "",
            default_value: "",
        },
        length: {
            name: "length",
            rules: "trim|max_length[100]|numeric[0]",
            max_len: "100",
            min_len: "",
            default_value: "",
        },
        default_value: {
            name: "default_value",
            rules: "trim|max_length[100]",
            max_len: "100",
            min_len: "",
            default_value: "",
        },
        attributes: {
            name: "attributes",
            rules: "trim|max_length[30]",
            max_len: "30",
            min_len: "",
            default_value: "",
        },
        index_type: {
            name: "index_type",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        aggregate: {
            name: "aggregate",
            rules: "trim|max_length[20]",
            max_len: "20",
            min_len: "",
            default_value: "",
        },
        comments: {
            name: "comments",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        date_sync: {
            name: "date_sync",
            rules: "trim",
            max_len: "",
            min_len: "",
            default_value: "",
        },
    },
};

// getters
const getters = {
    sql_fields: (state) => {
        return state.sql_fields;
    },
};

// actions
const actions = {
    async fetchSqlFields({ rootState, commit, dispatch }, payload = null) {
        try {
            let body = payload.body ? payload.body : {};
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            const response = await getSqlFields({ body: body, headers: config.headers });

            if (typeof response !== "undefined" && response.data) {
                if (response.data.page) {
                    commit("mutate", {
                        property: "page",
                        with: response.data.page,
                    });
                }

                if (body.table_id) {
                    commit("mutate", {
                        property: "sql_field_options",
                        with: response.data.sql_fields,
                    });
                } else {
                    commit("mutate", {
                        property: "sql_fields",
                        with: response.data.sql_fields,
                    });
                }

                return response.data;
            } else {
                console.log("fetch all response error");
                return;
            }
        } catch (error) {
            console.log("fetch all error");
            return;
        }
    },
    async fetchSqlField({ rootState, commit, dispatch }, payload = null) {
        try {
            let response = null;
            let body = payload.body ? payload.body : "";
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await getSqlField(payload.id);

            if (typeof response !== "undefined" && response.data) {
                commit("mutate", {
                    property: "selectedSqlField",
                    with: response.data.sql_field,
                });
                return response.data;
            } else {
                console.log("fetch response error");
                return;
            }
        } catch (error) {
            console.log("fetch single error");
            return;
        }
    },
    async printSqlField({ rootState, commit, dispatch }, payload = null) {
        try {
            let response = null;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await printSqlField(payload.id);

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("fetch print response error");
                return;
            }
        } catch (error) {
            console.log("print error");
            return;
        }
    },
    async saveSqlField({ rootState, commit, dispatch }, payload) {
        try {
            let response = null;
            let body = payload.body;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            if (typeof body["field_id"] === "undefined" || body["field_id"] == null || body["field_id"] == "") {
                delete body["field_id"];
                response = await createSqlFields({ body: body, headers: config.headers });
            } else {
                let field_id = body["field_id"];
                delete body["field_id"];
                response = await updateSqlFields({ body: body, headers: config.headers }, field_id);
            }

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("save response error");
                return;
            }
        } catch (error) {
            console.log("save error");
            return;
        }
    },
    async deleteSqlField({ rootState, commit, dispatch }, payload) {
        try {
            let response = null;
            let body = payload.body;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await deleteSqlFields(payload.id);

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("delete response error");
                return;
            }
        } catch (error) {
            console.log("delete error");
            return;
        }
    },
};

// mutations
const mutations = {
    mutate(state, payload) {
        state[payload.property] = payload.with;
    },
    resetSqlFields(state) {
        state.page = {};
        state.form = {};
        state.sql_fields = [];
        state.sql_field_options = [];
        state.selectedSqlField = {
            field_id: null,
            company_id: "",
            sql_field_date: "",
            reference_number: "",
            status_id: "",
            billing_address_id: "",
            shipping_address_id: "",
            comments: "",
            sql_field_details: [],
        };
        state.sql_fields_filter = {
            search: "",
            page_size: 2,
            page_num: 1,
        };
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
